import formatDecimal, {
  parseFormattedFloat,
  roundDownToMultiplier,
  roundUpToMultiplier,
} from '@paradigm/utils/formatDecimal';

import {
  MyQuoteItem,
  SimpleQuote,
  TableQuoteItem,
} from '#/unified-rfqs/ui/types';
import { GrfqLeg, GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';
import { GrfqBbo } from '#/unified-rfqs/entities/grfq/bbo';
import { getProduct } from '#/products/domain';
import { Bbo } from '#/unified-rfqs/entities/drfq/bbo';
import { DrfqLeg } from '#/unified-rfqs/entities/drfq/domain';
import { NullableSide } from '../drfq/quote-builder/modal-context';
import { AggGrfqQuote } from './quotes-contexts';

type Side = 'BUY' | 'SELL';

/**
 * Aggregate the first quotes on the array with _n_ unique prices.
 * If _n_ is not specified it goes through all quotes.
 *
 * Goes quote by quote, from first to last, aggregating quotes with
 * the same price that are adjacent. So, quotes order will change the
 * result.
 *
 * @returns AggGrfqQuote[].
 */
export function aggregateAdjacentByPrice(
  quotes: readonly GrfqQuote[],
  n = Infinity,
): AggGrfqQuote[] {
  const result: AggGrfqQuote[] = [];

  if (quotes.length === 0) return result;

  let quote = quotes[0]!;
  let currentPrice = quote.price;
  let currentQty = quote.remaining_quantity;
  let currentLegs = quote.legs;
  let currentCount = 1;
  let currentId = quote.id;

  for (let i = 1; i < quotes.length; ++i) {
    quote = quotes[i]!;
    if (quote.price !== currentPrice) {
      if (result.length === n - 1) break;
      result.push({
        ...quote,
        legs: currentLegs,
        numQuotes: currentCount,
        price: currentPrice,
        remaining_quantity: currentQty,
        id: currentId,
      });
      currentPrice = quote.price;
      currentCount = 1;
      currentQty = quote.remaining_quantity;
      currentLegs = quote.legs;
      currentId = quote.id;
    } else {
      currentCount += 1;
      currentQty = (
        Number.parseFloat(currentQty) +
        Number.parseFloat(quote.remaining_quantity)
      ).toString();
    }
  }

  result.push({
    ...quote,
    legs: currentLegs,
    price: currentPrice,
    numQuotes: currentCount,
    remaining_quantity: currentQty,
    id: currentId,
  });

  return result;
}

export function toSimpleQuote(quote: GrfqQuote): SimpleQuote {
  return [quote.remaining_quantity, quote.price, quote.id];
}

export interface ExecutionRange {
  readonly start: number;
  readonly end: number;
  readonly price: number;
}

export function calculateExecutionRanges({
  quotes,
  minBlockSize,
  minContractIncrement,
}: {
  readonly quotes: readonly SimpleQuote[];
  readonly minBlockSize: number;
  readonly minContractIncrement: number;
}): readonly ExecutionRange[] {
  return quotes.reduce<readonly ExecutionRange[]>(
    (ranges, [qty, price], index) => {
      const prevRange = ranges[index - 1];

      const offsetValue = prevRange?.end ?? 0;
      const start = roundUpToMultiplier(
        offsetValue + minBlockSize,
        minContractIncrement,
      );
      const end = roundDownToMultiplier(
        offsetValue + Number.parseFloat(qty),
        minContractIncrement,
      );

      const range = { start, end, price: Number.parseFloat(price) };
      return [...ranges, range];
    },
    [],
  );
}

/**
 * Aggregates quantities for the `quotes` that have a better or equal
 * price compared to the `price` option, depending on the `side`.
 */
export function aggregateQuotesQtyUntil(
  quotes: readonly GrfqQuote[],
  options: { readonly price: string; readonly side: Side },
): string {
  const { price, side } = options;

  const goodQuoteFilter =
    side === 'BUY'
      ? (quote: GrfqQuote) =>
          Number.parseFloat(quote.price) <= Number.parseFloat(price)
      : (quote: GrfqQuote) =>
          Number.parseFloat(quote.price) >= Number.parseFloat(price);

  const goodQuotes = quotes.filter(goodQuoteFilter);

  const aggQty = goodQuotes.reduce(
    (partialAggQty, quote) =>
      partialAggQty + Number.parseFloat(quote.remaining_quantity),
    0,
  );

  return aggQty.toString();
}

export function isOpen(quote: GrfqQuote): boolean {
  return quote.status === 'OPEN';
}

export function isMine(
  quote: GrfqQuote,
  myTicker: string | null | undefined,
): boolean {
  return myTicker != null && quote.maker === myTicker;
}

export function isNewer(current: GrfqQuote, next: GrfqQuote) {
  return (
    Number.parseFloat(next.remaining_quantity) <=
    Number.parseFloat(current.remaining_quantity)
  );
}

export function applyUpdate(current: GrfqQuote, next: GrfqQuote) {
  if (next.maker == null && current.maker != null)
    return { ...next, maker: current.maker };

  return next;
}

export function processAggregatedQuotes(
  quotes: readonly GrfqQuote[],
): TableQuoteItem[] {
  return aggregateAdjacentByPrice(quotes.filter(isOpen)).map(
    ({ remaining_quantity, price, id }, index) => ({
      key: `${remaining_quantity}-${price}-${index}`,
      quantity: remaining_quantity,
      price,
      id,
    }),
  );
}

export function processMyQuotes(quotes: readonly GrfqQuote[]): MyQuoteItem[] {
  return aggregateAdjacentByPrice(quotes.filter(isOpen)).map(
    ({ remaining_quantity, price, id }) => ({
      id,
      quantity: remaining_quantity,
      price,
    }),
  );
}

export interface BestQuoteView {
  readonly sideLabel: string;
  readonly quantity: string;
  readonly price: string;
}

export function bestQuoteView(
  side: Side,
  bbo: GrfqBbo | null,
  legs: readonly GrfqLeg[] | undefined,
) {
  const sideLabel = side === 'BUY' ? 'Bid' : 'Offer';
  if (bbo == null) return { sideLabel, quantity: '-', price: '-' };

  const { quantity, price } = side === 'BUY' ? bbo.best_bid : bbo.best_ask;
  if (Number(quantity) === 0 || areAllLegPricesZero(side, bbo, legs))
    return { sideLabel, quantity: '-', price: '-' };

  return {
    sideLabel,
    quantity,
    price,
  };
}

function areAllLegPricesZero(
  bboSide: Side,
  bbo: GrfqBbo,
  legs: readonly GrfqLeg[] | undefined,
) {
  if (bbo.leg_prices.length !== legs?.length) return false;
  if (bbo.leg_prices.length === 0) return false;

  return bbo.leg_prices.every((leg, index) => {
    const legSide = legs[index]?.side;
    let price = legSide === 'SELL' ? leg.best_ask_price : leg.best_bid_price;
    if (bboSide === 'SELL') {
      price = legSide === 'SELL' ? leg.best_bid_price : leg.best_ask_price;
    }

    return Number(price) === 0;
  });
}

export function getOppositeSide(side: Side): Side {
  return side === 'BUY' ? 'SELL' : 'BUY';
}

export function parseQtyToClearingUnities(
  quantity: string,
  productCode: string,
) {
  const qtyFloat = parseFormattedFloat(quantity);
  const product = getProduct(productCode);
  const currencyQty = formatDecimal(
    (qtyFloat * product.contractSize).toString(),
  );
  return { qty: currencyQty, currency: product.contractCurrency };
}

export function parseClearingUnitiesToQty(
  quantity: string,
  productCode: string,
) {
  const qtyFloat = parseFormattedFloat(quantity);
  const product = getProduct(productCode);
  const currencyQty = String(qtyFloat / product.contractSize);
  return { qty: currencyQty, currency: product.contractCurrency };
}

export function bestOrderView(
  side: Side,
  bbo: Bbo | null,
  legs: readonly DrfqLeg[] | undefined,
) {
  const isBuySide = side === 'BUY';
  const sideLabel = isBuySide ? 'Bid' : 'Offer';
  if (bbo == null) return { sideLabel, quantity: '-', price: '-' };

  const bboBest = isBuySide ? bbo.best_bid : bbo.best_ask;
  const quantity = bboBest?.quantity ?? '';
  const price = bboBest?.price ?? '';

  if (Number(quantity) === 0 || areAllDrfqLegPricesZero(bbo, legs))
    return { sideLabel, quantity: '-', price: '-' };

  return {
    sideLabel,
    quantity,
    price,
  };
}

function areAllDrfqLegPricesZero(
  bbo: Bbo,
  legs: readonly DrfqLeg[] | undefined,
) {
  if (bbo.legs.length !== legs?.length) return false;
  if (bbo.legs.length === 0) return false;

  return legs.every((leg) => {
    const { price } = leg;
    return Number(price) === 0;
  });
}

export function getPriceSide(modalSide: NullableSide, legSide: Side) {
  let side: Side = 'BUY';
  if (modalSide === 'SELL') {
    side = legSide === 'SELL' ? legSide : getOppositeSide(legSide);
  } else if (modalSide === 'BUY') {
    if (legSide === 'SELL') {
      side = getOppositeSide(legSide);
    }
  } else {
    side = legSide;
  }
  return side;
}
